import { useState, useMemo, FC } from 'react';
import { useSnackbar } from 'notistack';
import { Table, Pagination, Loader } from '../../../../components';
import { Button, Box } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { ISalesToolsEmailTemplate, ISalesToolsEmailTemplatesRes } from '../../../../models';
import { EmailTemplateModal } from '../modals/email-template-modal';
import { deleteSalesEmailTemplates, getSalesToolsEmailTemplates } from '../../../../fetch';
import { useQuery } from 'react-query';

interface IEmailTemplates {
  isModalOpen: boolean;
  setModalOpen: (val: boolean) => void;
}

export const EmailTemplates: FC<IEmailTemplates> = ({ isModalOpen, setModalOpen }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [currentEmailTemplate, setCurrentEmailTemplate] = useState<ISalesToolsEmailTemplate>(null);

  const [page, setPage] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);

  const {
    isLoading: isLoadingEmailTemplates,
    data: emailTemplates,
    refetch: refetchEmailTemplates
  } = useQuery<ISalesToolsEmailTemplatesRes, Error>(
    ['get-email-templates', page, perPage],
    () =>
      getSalesToolsEmailTemplates({
        page: page + 1,
        perPage
      }),
    {}
  );

  const handleDelete = async (id: number) => {
    const result = window.confirm('Are you sure you want to delete this email template?');
    if (result) {
      setDeleting(true);
      try {
        await deleteSalesEmailTemplates(id);
        refetchEmailTemplates();
        enqueueSnackbar('Email Template Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(
          error?.Detail ?? 'Error, unable to delete email template, please try again',
          {
            variant: 'error'
          }
        );
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISalesToolsEmailTemplate } };
        }) => {
          return (
            <Box display="flex" gap={0.5}>
              <Button
                disabled={isDeleting}
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  setCurrentEmailTemplate(original);
                  setModalOpen(true);
                }}
              >
                Edit
              </Button>
              {!original.isMasterTemplate && (
                <Button
                  disabled={isDeleting}
                  sx={{
                    color: theme => theme.palette.error.main
                  }}
                  startIcon={<Delete />}
                  onClick={() => {
                    handleDelete(original?.id);
                  }}
                >
                  Delete
                </Button>
              )}
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTemplates, isDeleting]);

  return (
    <>
      {(isLoadingEmailTemplates || !emailTemplates || isDeleting) && (
        <Loader position="centered" type="overlay">
          Loading...
        </Loader>
      )}
      {emailTemplates && (
        <>
          <Table
            columns={columns}
            data={emailTemplates?.records}
            isLoading={isLoadingEmailTemplates}
            hidePagination
            mobileProps={{
              handleEdit: (val: ISalesToolsEmailTemplate) => setCurrentEmailTemplate(val),
              handleDelete: val => handleDelete(val.territoryId)
            }}
          />
          <Pagination
            page={page}
            count={emailTemplates?.totalRecordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setPerPage}
          />
        </>
      )}
      {isModalOpen && (
        <EmailTemplateModal
          onClose={() => {
            setModalOpen(false);
            setTimeout(() => {
              setCurrentEmailTemplate(null);
            }, 250);
          }}
          open
          refetchEmailTemplates={refetchEmailTemplates}
          emailTemplateId={currentEmailTemplate?.id}
        />
      )}
    </>
  );
};
